import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MeetingDraftService } from '../../../../../core/services/meeting-draft.service';
import { LocaleService } from '../../../../../core/services/locale.service';
import { isScheduleRoute, SCHEDULE_ROUTE } from '../../../../../core/utils/schedule-link';
import { readPendingIntent } from '../../../../schedule-preview/data/pending-intent';
import { formatPrefs } from '../../../../../core/utils/format-prefs';
import { formatWallClock } from '../../../../../core/utils/locale-date';
import {
  PaperSidePanelComponent,
  PaperSidePanelHeadComponent,
  PaperSidePanelBodyComponent,
  PaperSidePanelFootComponent,
  PaperButtonComponent,
  PaperIconComponent,
} from '@ricoh-paper/angular';

/**
 * BmpDraftsLibraryComponent — slide-out side panel listing every
 * minimised meeting draft the viewer has on the server. Opened via
 * `MeetingDraftService.openLibrary()` (the dock's "View all" link or
 * a future command-palette entry). Each row surfaces the draft's
 * title, a meta line (date + duration + relative updatedAt), a
 * Restore button, and a Delete affordance.
 *
 * RESPONSIBILITY: Read-only renderer over `meetingDraftService.drafts`
 * plus two thin emitters (restoreRequest, remove). The service owns
 * the data; the panel owns the affordance.
 *
 * USED BY: AppShellComponent (mounted alongside the existing dock so
 * the library follows the user across navigation).
 *
 * AI CONTEXT:
 *   - Restore triggers the existing `requestRestore` handshake that
 *     `BookMeetingPanelComponent`'s `draftRestoreEffect` already
 *     consumes — no new wiring required.
 *   - Delete fires the optimistic remove; rollback handling lives in
 *     the service.
 *   - We tick a 60s signal so the "Updated 2 min ago" copy stays
 *     accurate without re-rendering on every store change.
 */
@Component({
  selector: 'bmp-drafts-library',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    PaperSidePanelComponent,
    PaperSidePanelHeadComponent,
    PaperSidePanelBodyComponent,
    PaperSidePanelFootComponent,
    PaperButtonComponent,
    PaperIconComponent,
    TranslocoPipe,
  ],
  template: `
    <paper-side-panel
      [open]="meetingDrafts.libraryOpen()"
      [width]="420"
      [side]="'right'"
      [title]="meetingDraftsTitle()"
      (closed)="onClosed()">
      <paper-side-panel-head>
        <span slot="eyebrow">{{ 'viewer.book_meeting_panel.drafts_library.eyebrow' | transloco }}</span>
        <h2 slot="title">{{ 'viewer.book_meeting_panel.drafts_library.title' | transloco }}</h2>
        <span slot="sub">{{ draftCountLabel() }}</span>
      </paper-side-panel-head>

      <paper-side-panel-body>
        @if (drafts().length === 0) {
          <div class="bmp-drafts-empty" data-testid="bmp-drafts-library-empty">
            <div class="bmp-drafts-empty__icon" aria-hidden="true">
              <paper-icon name="calendar" [size]="24"></paper-icon>
            </div>
            <p class="bmp-drafts-empty__title">{{ 'viewer.book_meeting_panel.drafts_library.no_drafts_yet' | transloco }}</p>
            <p class="bmp-drafts-empty__sub">
              {{ 'viewer.book_meeting_panel.drafts_library.no_drafts_sub' | transloco }}
            </p>
          </div>
        } @else {
          <ul
            class="bmp-drafts-list"
            role="list"
            data-testid="bmp-drafts-library-list">
            @for (draft of drafts(); track draft.id) {
              <li class="bmp-drafts-item">
                <button
                  type="button"
                  class="bmp-drafts-item__body"
                  [attr.aria-label]="rowAria(draft)"
                  [attr.data-testid]="'bmp-drafts-library-row-' + draft.id"
                  [attr.data-held]="isHeld(draft) ? 'true' : null"
                  (click)="onRestore(draft.id)">
                  <span class="bmp-drafts-item__title">
                    {{ draft.title || untitledMeetingLabel() }}
                  </span>
                  <span class="bmp-drafts-item__meta">{{ metaFor(draft) }}</span>
                  <!-- A HELD ROW IS NOT A DRAFT. Its meeting is already
                       booked and only its order is waiting, so the row has
                       to say so — otherwise the list invites somebody to
                       "restore" a room they already hold. -->
                  @if (isHeld(draft)) {
                    <span class="bmp-drafts-item__held">{{ heldLabel() }}</span>
                  }
                </button>
                <div class="bmp-drafts-item__actions">
                  <paper-button
                    [id]="'bmp-drafts-restore-' + draft.id"
                    intent="secondary"
                    size="xs"
                    [attr.data-testid]="'bmp-drafts-library-restore-' + draft.id"
                    (click)="onRestore(draft.id)">
                    {{ 'viewer.book_meeting_panel.drafts_library.restore' | transloco }}
                  </paper-button>
                  <paper-button
                    [id]="'bmp-drafts-delete-' + draft.id"
                    intent="ghost"
                    size="xs"
                    shape="icon"
                    icon="trash"
                    [iconSize]="13"
                    [ariaLabel]="deleteDraftAria(draft.title)"
                    [attr.data-testid]="'bmp-drafts-library-delete-' + draft.id"
                    (click)="onDelete(draft.id)" />
                </div>
              </li>
            }
          </ul>
        }
      </paper-side-panel-body>

      @if (drafts().length > 0) {
        <paper-side-panel-foot>
          <span class="bmp-drafts-foot-meta">
            {{ 'viewer.book_meeting_panel.drafts_library.sync_note' | transloco }}
          </span>
          <paper-button
            id="bmp-drafts-library-close"
            intent="secondary"
            size="sm"
            (click)="meetingDrafts.closeLibrary()">
            {{ 'viewer.book_meeting_panel.drafts_library.done' | transloco }}
          </paper-button>
        </paper-side-panel-foot>
      }
    </paper-side-panel>
  `,
  styles: `
    :host { display: contents; }

    .bmp-drafts-empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      gap: 8px;
      padding: 40px 24px;
    }
    .bmp-drafts-empty__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 44px;
      height: 44px;
      border-radius: 50%;
      background: color-mix(in srgb, var(--color-primary) 12%, transparent);
      color: var(--color-primary);
      margin-bottom: 4px;
    }
    .bmp-drafts-empty__title {
      margin: 0;
      font-size: 14px;
      font-weight: 600;
      color: var(--text);
    }
    .bmp-drafts-empty__sub {
      margin: 0;
      font-size: 12.5px;
      color: var(--text-tertiary);
      line-height: 1.4;
      max-width: 28ch;
    }

    .bmp-drafts-list {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 6px;
    }
    .bmp-drafts-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 10px;
      border: 1px solid var(--divider);
      border-radius: 10px;
      background: var(--surface-raised, var(--surface-1));
      transition: border-color var(--motion-fast, 120ms) var(--ease, ease),
                  background var(--motion-fast, 120ms) var(--ease, ease);

      &:hover {
        border-color: var(--border-med, var(--divider));
        background: var(--surface-2);
      }
    }
    .bmp-drafts-item__body {
      flex: 1;
      min-width: 0;
      display: flex;
      flex-direction: column;
      gap: 2px;
      background: transparent;
      border: none;
      text-align: left;
      cursor: pointer;
      padding: 4px 0;
      color: inherit;
      font: inherit;

      &:focus-visible {
        outline: 2px solid var(--brand-focus-ring, var(--color-primary));
        outline-offset: 2px;
        border-radius: 6px;
      }
    }
    .bmp-drafts-item__title {
      font-size: 13.5px;
      font-weight: 600;
      color: var(--text);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .bmp-drafts-item__meta {
      font-size: 11.5px;
      color: var(--text-tertiary);
    }
    /* Held rows read as a STATE, not as an alert — the meeting is fine, the
       order is simply waiting. Brand ink, not a warning colour. */
    .bmp-drafts-item__held {
      display: block;
      margin-top: 2px;
      font-size: 11.5px;
      font-weight: 600;
      color: var(--color-primary);
    }
    .bmp-drafts-item__actions {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      flex: none;
    }
    .bmp-drafts-foot-meta {
      font-size: 11.5px;
      color: var(--text-tertiary);
      flex: 1;
      line-height: 1.4;
    }

    @media (prefers-reduced-motion: reduce) {
      .bmp-drafts-item { transition: none; }
    }
  `,
})
export class BmpDraftsLibraryComponent {
  protected readonly meetingDrafts = inject(MeetingDraftService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly locale = inject(LocaleService);

  /** Ticks every minute to refresh the "Updated 2 min ago" copy. */
  private readonly minuteTick = signal(0);

  protected readonly drafts = this.meetingDrafts.drafts;

  protected readonly meetingDraftsTitle = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.drafts_library.title');
  });

  protected readonly untitledMeetingLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.drafts_library.untitled_meeting');
  });

  protected readonly heldLabel = computed(() => {
    this.locale.locale();
    return this.transloco.translate('viewer.book_meeting_panel.drafts_library.held_meta');
  });

  /**
   * Is this row a parked composition, or a BOOKED meeting whose order is
   * still waiting on the calendar?
   *
   * The second is not a draft in any sense a person would recognise. It is
   * kept in the same store because the intent had to live somewhere the next
   * load could find it, and this is what stops that implementation detail
   * reading as "you have an unfinished meeting".
   */
  protected isHeld(draft: { payload?: Record<string, unknown> }): boolean {
    return !!readPendingIntent(draft.payload);
  }

  protected rowAria(draft: { title?: string; payload?: Record<string, unknown> }): string {
    const title = draft.title || this.untitledMeetingLabel();
    return this.isHeld(draft)
      ? this.transloco.translate('viewer.book_meeting_panel.drafts_library.held_aria', { title })
      : this.restoreDraftAria(title);
  }

  protected restoreDraftAria(title?: string): string {
    return this.transloco.translate('viewer.book_meeting_panel.drafts_library.restore_aria', {
      title: title || this.untitledMeetingLabel(),
    });
  }

  protected deleteDraftAria(title?: string): string {
    return this.transloco.translate('viewer.book_meeting_panel.drafts_library.delete_aria', {
      title: title || this.untitledMeetingLabel(),
    });
  }

  protected readonly draftCountLabel = computed(() => {
    this.locale.locale();
    const count = this.drafts().length;
    if (count === 0) return this.transloco.translate('viewer.book_meeting_panel.drafts_library.no_drafts_saved');
    return this.transloco.translate('viewer.book_meeting_panel.drafts_library.n_drafts', { count });
  });

  constructor() {
    const interval = setInterval(
      () => this.minuteTick.update(t => t + 1),
      60_000,
    );
    this.destroyRef.onDestroy(() => clearInterval(interval));
  }

  /** Resume → the schedule redesign's creation panel (the only restore
   *  consumer). Navigate there when the library was opened on another
   *  route; the pending request survives navigation as a signal. */
  protected onRestore(id: string): void {
    this.meetingDrafts.requestRestore(id, 'library');
    this.meetingDrafts.closeLibrary();
    if (!isScheduleRoute(this.router.url)) {
      void this.router.navigate([SCHEDULE_ROUTE]);
    }
  }

  protected onDelete(id: string): void {
    this.meetingDrafts.remove(id);
  }

  protected onClosed(): void {
    this.meetingDrafts.closeLibrary();
  }

  /** Render the meta line for a draft row. Read minuteTick so the
   *  relative timestamp refreshes without re-rendering every signal
   *  change in the service. */
  protected metaFor(draft: {
    startMin: number;
    durationMin: number;
    dateLabel?: string;
    updatedAt: string;
  }): string {
    this.minuteTick();
    const time = this.formatTime(draft.startMin);
    const dur = this.formatDur(draft.durationMin);
    const updated = this.formatRelative(draft.updatedAt);
    const date = draft.dateLabel;
    return [date, time, dur, updated].filter(Boolean).join(' · ');
  }

  private formatTime(min: number): string {
    if (min === 0) return '—';
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (formatPrefs().timeFormat === 'h23') return formatWallClock(h, m);
    const ampm = h >= 12 ? 'pm' : 'am';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return m === 0 ? `${h12}${ampm}` : `${h12}:${String(m).padStart(2, '0')}${ampm}`;
  }

  private formatDur(min: number): string {
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h${m}m`;
  }

  private formatRelative(iso: string): string {
    if (!iso) return '';
    const updated = new Date(iso).getTime();
    if (Number.isNaN(updated)) return '';
    const elapsed = Date.now() - updated;
    this.locale.locale();
    const t = (k: string, params?: Record<string, unknown>) => this.transloco.translate(k, params);
    if (elapsed < 60_000) return t('viewer.book_meeting_panel.drafts_library.just_now');
    const mins = Math.floor(elapsed / 60_000);
    if (mins < 60) return t('viewer.book_meeting_panel.drafts_library.n_min_ago', { count: mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('viewer.book_meeting_panel.drafts_library.n_hr_ago', { count: hrs });
    const days = Math.floor(hrs / 24);
    return t('viewer.book_meeting_panel.drafts_library.n_days_ago', { count: days });
  }
}
